// What a Focus surface says for an `error` timeline entry, and for a failed
// apply.
//
// The server's `error` event carries a closed `code` (`USER_ERROR_CODES`) and a
// static English `message` meant for surfaces with no catalog. Focus shows the
// catalog line for the code instead, in the user's own language, and the
// generic line for a code it does not know — never the wire `message`, which
// an older server filled with an exception's text. The one exception is an
// entry this client built itself (`localized`): its message already IS catalog
// copy.
//
// A failed `readiness_apply_result` is read the same way: its `code`
// (`APPLY_ERROR_CODES`), else the reason at the head of an older server's
// `error`, else `apply_failed` — and the screen shows that code's catalog line,
// never `error`.

import {
  applyErrorCodeOf,
  isApplyErrorCode,
  isUserErrorCode,
  type ApplyErrorCode,
} from "@loom/shared";

import type { MessageKey, Translator } from "../i18n/index.js";

/** The catalog key for a server error code; `error.generic` for anything else. */
export const errorCopyKey = (code?: string): MessageKey =>
  isUserErrorCode(code) ? `error.${code}` : "error.generic";

/** The line to show for one error entry. */
export const errorText = (
  e: { readonly message: string; readonly code?: string; readonly localized?: true },
  t: Translator,
): string => (e.localized ? e.message : t(errorCopyKey(e.code)));

/** The closed reason of a failed apply: its `code` when this client knows it,
 *  else the head of `error` (an older server sent no code), else
 *  `apply_failed`. Reads the head token only, never the text behind it. */
export const applyErrorCode = (r: {
  readonly code?: string;
  readonly error?: string;
}): ApplyErrorCode =>
  isApplyErrorCode(r.code) ? r.code : applyErrorCodeOf(r.error ?? "");

/** The catalog key for a failed apply's reason. */
export const applyErrorCopyKey = (code: ApplyErrorCode): MessageKey =>
  `error.apply.${code}`;

// Whether Retry can mend a failed apply. Retry re-sends the SAME accept token,
// so only a passing failure can turn out differently: a write error, an edit
// not folded in yet, an access check. The rest refuse that token the same way
// every time; their copy sends the user to refine again, and the screen offers
// that in Retry's place. A Record, so a new code has to be decided here.
const APPLY_RETRY_HELPS: Readonly<Record<ApplyErrorCode, boolean>> = {
  apply_failed: true,
  edit_pending: true,
  access_denied: true,
  card_already_applied: false,
  accept_token_unverified: false,
  accept_token_required: false,
  card_lost: false,
  reconstruct_failed: false,
};

export const applyRetryHelps = (code: ApplyErrorCode): boolean => APPLY_RETRY_HELPS[code];
